/**
 * ID Utilities - Helper functions per ID tipizzati
 *
 * Ogni ID ha un prefisso che indica il tipo di entità
 * Formato: {prefix}-{uniqueId}
 * Esempio: prj-abc123, tsk-def456, usr-ghi789
 */

import { ID_PREFIXES, type IdPrefix } from './idPrefixes';

// =============================================================================
// ID GENERATION
// =============================================================================

/**
 * Generate unique ID with prefix
 *
 * @param prefix - ID prefix (es: 'prj', 'tsk', 'usr')
 * @returns ID formattato (es: 'prj-abc123xyz')
 *
 * @example
 * genId('prj') // => 'prj-7f3k9m2n'
 * genId('tsk') // => 'tsk-x1y2z3a4'
 */
export function genId(prefix: IdPrefix): string {
  const uniquePart = generateUniqueString();
  return `${prefix}-${uniquePart}`;
}

/**
 * Generate multiple IDs at once
 */
export function genIds(prefix: IdPrefix, count: number): string[] {
  return Array.from({ length: count }, () => genId(prefix));
}

// =============================================================================
// ID VALIDATION
// =============================================================================

/**
 * Validate ID format
 *
 * @param id - ID to validate
 * @param expectedPrefix - Optional expected prefix
 * @returns true if valid
 *
 * @example
 * validateId('prj-abc123') // => true
 * validateId('invalid') // => false
 * validateId('prj-abc123', 'prj') // => true
 * validateId('prj-abc123', 'tsk') // => false
 */
export function validateId(id: string, expectedPrefix?: IdPrefix): boolean {
  if (!id || typeof id !== 'string') return false;

  const parts = id.split('-');
  if (parts.length < 2) return false;

  const prefix = parts[0];
  const uniquePart = parts.slice(1).join('-');

  // Check prefix is valid
  const validPrefixes = Object.values(ID_PREFIXES);
  if (!validPrefixes.includes(prefix as any)) return false;

  // Check unique part exists
  if (!uniquePart || uniquePart.length === 0) return false;

  // Check expected prefix if provided
  if (expectedPrefix && prefix !== expectedPrefix) return false;

  return true;
}

/**
 * Validate multiple IDs
 */
export function validateIds(ids: string[], expectedPrefix?: IdPrefix): boolean {
  return ids.every((id) => validateId(id, expectedPrefix));
}

// =============================================================================
// ID EXTRACTION
// =============================================================================

/**
 * Extract prefix from ID
 *
 * @param id - ID to extract from
 * @returns Prefix or null if invalid
 *
 * @example
 * extractPrefix('prj-abc123') // => 'prj'
 * extractPrefix('invalid') // => null
 */
export function extractPrefix(id: string): IdPrefix | null {
  if (!id || typeof id !== 'string') return null;

  const parts = id.split('-');
  if (parts.length < 2) return null;

  const prefix = parts[0];
  const validPrefixes = Object.values(ID_PREFIXES);

  return validPrefixes.includes(prefix as any) ? (prefix as IdPrefix) : null;
}

/**
 * Extract unique part from ID (without prefix)
 */
export function extractUniquePart(id: string): string | null {
  if (!validateId(id)) return null;

  const parts = id.split('-');
  return parts.slice(1).join('-');
}

// =============================================================================
// ID TYPE CHECKING
// =============================================================================

/**
 * Check if ID is of specific type
 */
export function isProjectId(id: string): boolean {
  return extractPrefix(id) === ID_PREFIXES.prj;
}

export function isTaskId(id: string): boolean {
  return extractPrefix(id) === ID_PREFIXES.tsk;
}

export function isContactId(id: string): boolean {
  return extractPrefix(id) === ID_PREFIXES.cnt;
}

export function isUserId(id: string): boolean {
  return extractPrefix(id) === ID_PREFIXES.usr;
}

export function isInvoiceId(id: string): boolean {
  return extractPrefix(id) === ID_PREFIXES.inv;
}

export function isConversationId(id: string): boolean {
  return extractPrefix(id) === ID_PREFIXES.cnv;
}

export function isMessageId(id: string): boolean {
  return extractPrefix(id) === ID_PREFIXES.msg;
}

export function isTimeEntryId(id: string): boolean {
  return extractPrefix(id) === ID_PREFIXES.tme;
}

export function isAccountId(id: string): boolean {
  return extractPrefix(id) === ID_PREFIXES.acc;
}

// =============================================================================
// ID FILTERING & GROUPING
// =============================================================================

/**
 * Filter IDs by prefix
 *
 * @example
 * const ids = ['prj-1', 'tsk-2', 'prj-3']
 * filterByPrefix(ids, 'prj') // => ['prj-1', 'prj-3']
 */
export function filterByPrefix(ids: string[], prefix: IdPrefix): string[] {
  return ids.filter((id) => extractPrefix(id) === prefix);
}

/**
 * Group IDs by prefix
 *
 * @example
 * const ids = ['prj-1', 'tsk-2', 'prj-3', 'tsk-4']
 * groupByPrefix(ids)
 * // => { 'prj': ['prj-1', 'prj-3'], 'tsk': ['tsk-2', 'tsk-4'] }
 */
export function groupByPrefix(ids: string[]): Record<string, string[]> {
  const groups: Record<string, string[]> = {};

  for (const id of ids) {
    const prefix = extractPrefix(id);
    if (prefix) {
      if (!groups[prefix]) {
        groups[prefix] = [];
      }
      groups[prefix].push(id);
    }
  }

  return groups;
}

// =============================================================================
// PRIVATE HELPERS
// =============================================================================

/**
 * Generate unique random string
 * Uses combination of timestamp and random chars for uniqueness
 */
function generateUniqueString(length = 8): string {
  const timestamp = Date.now().toString(36); // base36 encoding
  const randomPart = generateRandomString(length - timestamp.length);
  return (timestamp + randomPart).slice(0, length);
}

/**
 * Generate random alphanumeric string
 */
function generateRandomString(length: number): string {
  const chars = 'abcdefghijklmnopqrstuvwxyz0123456789';
  let result = '';

  // Use crypto.getRandomValues if available (browser)
  if (typeof crypto !== 'undefined' && crypto.getRandomValues) {
    const array = new Uint8Array(length);
    crypto.getRandomValues(array);

    for (let i = 0; i < length; i++) {
      result += chars[array[i] % chars.length];
    }
  } else {
    // Fallback to Math.random
    for (let i = 0; i < length; i++) {
      result += chars[Math.floor(Math.random() * chars.length)];
    }
  }

  return result;
}
